import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Upload as UploadIcon, 
  FileText, 
  Link, 
  Mail, 
  Video,
  Clipboard,
  X
} from 'lucide-react';
import api from '../api/client';
import endpoints from '../api/endpoints';
import toast from 'react-hot-toast';
import { DROPZONE_ACCEPT, SOURCE_TYPES } from '../constants/contentTypes';
import '../assets/css/upload.css';

const Upload = () => {
  const [activeTab, setActiveTab] = useState('file');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedEmailFile, setSelectedEmailFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    contentType: 'PDF',
    sourceType: 'Research Paper',
    videoUrl: '',
    pastedContent: ''
  });
  const [loading, setLoading] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: DROPZONE_ACCEPT.document,
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setSelectedFile(acceptedFiles[0]);
        if (!formData.title) {
          setFormData(prev => ({ ...prev, title: acceptedFiles[0].name }));
        }
      }
    }
  });

  // Email file parsing removed - server will handle parsing
  // Form fields remain enabled for manual entry even when file is uploaded

  const { getRootProps: getEmailRootProps, getInputProps: getEmailInputProps, isDragActive: isEmailDragActive } = useDropzone({
    accept: DROPZONE_ACCEPT.email,
    maxFiles: 1,
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setSelectedEmailFile(file);
        toast.success('Email file selected! Server will process it upon upload.');
      } else if (rejectedFiles.length > 0) {
        toast.error('Please select a valid email file (.eml or .msg). Save your email from Outlook first.');
      }
    }
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;

    setLoading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', selectedFile);
      uploadData.append('title', formData.title);
      uploadData.append('description', formData.description);
      uploadData.append('contentType', formData.contentType);
      uploadData.append('sourceType', formData.sourceType);

      // Don't manually set Content-Type - let axios set it with the boundary
      await api.post(endpoints.upload.document, uploadData);

      toast.success('Document uploaded successfully!');
      setSelectedFile(null);
      setFormData({
        title: '',
        description: '',
        contentType: 'PDF',
        sourceType: 'Research Paper'
      });
    } catch (error) {
      console.error('Upload error:', error);
      // Display detailed error message from server
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to upload document';
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleUrlSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(endpoints.upload.url, {
        url: formData.url,
        title: formData.title,
        description: formData.description,
        contentType: formData.contentType,
        sourceType: formData.sourceType
      });

      toast.success('URL content processed successfully!');
      setFormData({
        title: '',
        description: '',
        contentType: 'Website',
        sourceType: 'External Source',
        url: ''
      });
    } catch (error) {
      console.error('URL processing error:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to process URL';
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    // Validate: either file OR manual fields must be provided
    if (!selectedEmailFile) {
      // No file - validate manual entry fields
      if (!formData.subject || !formData.sender || !formData.date || !formData.body) {
        toast.error('Please upload an email file OR fill in all manual entry fields');
        return;
      }
    }
    
    setLoading(true);
    try {
      if (selectedEmailFile) {
        // Handle email file upload
        const uploadData = new FormData();
        uploadData.append('file', selectedEmailFile);
        uploadData.append('contentType', formData.contentType);
        uploadData.append('sourceType', formData.sourceType);
        uploadData.append('description', formData.description);

        // Don't manually set Content-Type - let axios set it with the boundary
        await api.post(endpoints.upload.email, uploadData);

        toast.success('Email file processed successfully!');
        setSelectedEmailFile(null);
      } else {
        // Handle manual email entry
        await api.post(endpoints.upload.email, {
          subject: formData.subject,
          emailBody: formData.body,
          sender: formData.sender,
          date: formData.date,
          contentType: formData.contentType,
          sourceType: formData.sourceType,
          description: formData.description
        });

        toast.success('Email content processed successfully!');
      }

      setFormData({
        title: '',
        description: '',
        contentType: 'Email',
        sourceType: 'Internal Report',
        subject: '',
        body: '',
        sender: '',
        date: ''
      });
    } catch (error) {
      console.error('Email processing error:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to process email';
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(endpoints.upload.youtube, {
        url: formData.videoUrl,
        title: formData.title,
        description: formData.description,
        contentType: formData.contentType,
        sourceType: formData.sourceType
      });

      toast.success('Video content processed successfully!');
      setFormData({
        title: '',
        description: '',
        contentType: 'Video',
        sourceType: 'External Source',
        videoUrl: ''
      });
    } catch (error) {
      console.error('Video processing error:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to process video content';
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handlePasteSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(endpoints.upload.document, {
        title: formData.title,
        content: formData.pastedContent,
        contentType: formData.contentType,
        sourceType: formData.sourceType,
        description: formData.description
      });

      toast.success('Content uploaded successfully!');
      setFormData({
        title: '',
        description: '',
        contentType: 'Text',
        sourceType: 'Manual Entry',
        pastedContent: ''
      });
    } catch (error) {
      console.error('Paste upload error:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to upload content';
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  const removeEmailFile = () => {
    setSelectedEmailFile(null);
    // Don't clear form fields - user may have entered data manually
  };

  return (
    <div className="upload-container">
      <h1 className="upload-title">Upload Content to Nick</h1>

      <div className="upload-tabs">
        <button 
          className={`upload-tab ${activeTab === 'file' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('file')}
        >
          <FileText size={20} />
          File Upload
        </button>
        <button 
          className={`upload-tab ${activeTab === 'url' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('url')}
        >
          <Link size={20} />
          Website URL
        </button>
        <button 
          className={`upload-tab ${activeTab === 'email' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('email')}
        >
          <Mail size={20} />
          Email Content
        </button>
        <button 
          className={`upload-tab ${activeTab === 'video' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('video')}
        >
          <Video size={20} />
          Video Content
        </button>
        <button 
          className={`upload-tab ${activeTab === 'paste' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('paste')}
        >
          <Clipboard size={20} />
          Paste Content
        </button>
      </div>

      <div className="upload-section">
        {activeTab === 'file' && (
          <>
            <div {...getRootProps({ className: `dropzone${isDragActive ? ' is-active' : ''}` })}>
              <input {...getInputProps()} />
              <div className="dropzone-icon">
                <UploadIcon size={32} />
              </div>
              <div className="dropzone-text">
                {isDragActive ? 'Drop the file here' : 'Drag & drop a file here, or click to select'}
              </div>
              <div className="dropzone-subtext">
                Supports PDF, DOC, DOCX, TXT, HTML, and Images (JPG, PNG, GIF, TIFF) • Max 50MB
              </div>
            </div>

            {selectedFile && (
              <div className="file-preview">
                <div className="file-info">
                  <div className="file-icon">
                    <FileText size={20} />
                  </div>
                  <div className="file-details">
                    <div className="file-name">{selectedFile.name}</div>
                    <div className="file-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</div>
                  </div>
                  <button className="remove-button" onClick={removeFile}>
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}

            <div style={{ 
              marginTop: '16px', 
              padding: '12px', 
              background: 'var(--color-background)', 
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-border)'
            }}>
              💡 <strong>Tip:</strong> PDFs and images are processed with OCR for text extraction. Keep documents under 100 pages for best results. Very large documents are automatically sampled for search indexing.
            </div>

            <form className="upload-form" onSubmit={handleFileUpload}>
              <div className="upload-form-group">
                <label className="upload-label" htmlFor="title">Document Title</label>
                <input
                  className="upload-input"
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter document title"
                  required
                />
              </div>

              <div className="upload-form-group">
                <label className="upload-label" htmlFor="description">Description (Optional)</label>
                <textarea
                  className="upload-textarea"
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter document description"
                />
              </div>

              <div className="upload-row">
                <div className="upload-form-group upload-col">
                  <label className="upload-label" htmlFor="contentType">Content Type</label>
                  <select
                    className="upload-select"
                    id="contentType"
                    name="contentType"
                    value={formData.contentType}
                    onChange={handleInputChange}
                  >
                    <option value="PDF">PDF</option>
                    <option value="Website">Website</option>
                    <option value="Email">Email</option>
                    <option value="Video">Video</option>
                    <option value="Meeting Notes">Meeting Notes</option>
                    <option value="Study">Study</option>
                  </select>
                </div>

                <div className="upload-form-group upload-col">
                  <label className="upload-label" htmlFor="sourceType">Source Type</label>
                  <select
                    className="upload-select"
                    id="sourceType"
                    name="sourceType"
                    value={formData.sourceType}
                    onChange={handleInputChange}
                  >
                    {SOURCE_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button className="upload-button" type="submit" disabled={!selectedFile || loading}>
                {loading ? 'Uploading...' : 'Upload Document'}
              </button>
            </form>
          </>
        )}

        {activeTab === 'url' && (
          <form className="upload-form" onSubmit={handleUrlSubmit}>
            <div className="upload-form-group">
              <label className="upload-label" htmlFor="url">Website URL</label>
              <input
                className="upload-input"
                type="url"
                id="url"
                name="url"
                value={formData.url || ''}
                onChange={handleInputChange}
                placeholder="https://example.com"
                required
              />
            </div>

            <div className="upload-form-group">
              <label className="upload-label" htmlFor="title">Title</label>
              <input
                className="upload-input"
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter title for this content"
                required
              />
            </div>

            <div className="upload-form-group">
              <label className="upload-label" htmlFor="description">Description (Optional)</label>
              <textarea
                className="upload-textarea"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter description"
              />
            </div>

            <div className="upload-row">
              <div className="upload-form-group upload-col">
                <label className="upload-label" htmlFor="contentType">Content Type</label>
                <select
                  className="upload-select"
                  id="contentType"
                  name="contentType"
                  value={formData.contentType}
                  onChange={handleInputChange}
                >
                  <option value="Website">Website</option>
                  <option value="PDF">PDF</option>
                  <option value="Email">Email</option>
                  <option value="Video">Video</option>
                  <option value="Meeting Notes">Meeting Notes</option>
                  <option value="Study">Study</option>
                </select>
              </div>

              <div className="upload-form-group upload-col">
                <label className="upload-label" htmlFor="sourceType">Source Type</label>
                <select
                  className="upload-select"
                  id="sourceType"
                  name="sourceType"
                  value={formData.sourceType}
                  onChange={handleInputChange}
                >
                  {SOURCE_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            <button className="upload-button" type="submit" disabled={loading}>
              {loading ? 'Processing...' : 'Process URL'}
            </button>
          </form>
        )}

        {activeTab === 'email' && (
          <>
            <div {...getEmailRootProps({ className: `dropzone${isEmailDragActive ? ' is-active' : ''}` })}>
              <input {...getEmailInputProps()} />
              <div className="dropzone-icon">
                <Mail size={32} />
              </div>
              <div className="dropzone-text">
                {isEmailDragActive ? 'Drop the email file here' : 'Drag & drop an email file (.eml, .msg) here, or click to select'}
              </div>
              <div className="dropzone-subtext">
                Supported formats: EML, MSG
              </div>
              <div className="dropzone-help" style={{ marginTop: '12px', fontSize: '0.875rem', color: 'var(--color-text-secondary)', lineHeight: '1.5' }}>
                <strong>How to save from Outlook:</strong><br />
                • Desktop: Right-click email → Save As → Choose "Outlook Message Format (.msg)"<br />
                • Web: Open email → More actions (···) → Download → Save as .eml file
              </div>
            </div>

            {selectedEmailFile && (
              <div className="file-preview">
                <div className="file-info">
                  <div className="file-icon">
                    <Mail size={20} />
                  </div>
                  <div className="file-details">
                    <div className="file-name">{selectedEmailFile.name}</div>
                    <div className="file-size">{(selectedEmailFile.size / 1024).toFixed(2)} KB</div>
                  </div>
                  <button className="remove-button" onClick={removeEmailFile} type="button">
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}

            <div className="upload-divider">
              <span>OR enter email details manually</span>
            </div>

            <form className="upload-form" onSubmit={handleEmailSubmit}>
            <div className="upload-form-group">
              <label className="upload-label" htmlFor="subject">
                Email Subject {!selectedEmailFile && <span style={{color: 'red'}}>*</span>}
              </label>
              <input
                className="upload-input"
                type="text"
                id="subject"
                name="subject"
                value={formData.subject || ''}
                onChange={handleInputChange}
                placeholder={selectedEmailFile ? "Will be extracted from file" : "Enter email subject"}
              />
            </div>

            <div className="upload-form-group">
              <label className="upload-label" htmlFor="sender">
                Sender {!selectedEmailFile && <span style={{color: 'red'}}>*</span>}
              </label>
              <input
                className="upload-input"
                type="email"
                id="sender"
                name="sender"
                value={formData.sender || ''}
                onChange={handleInputChange}
                placeholder={selectedEmailFile ? "Will be extracted from file" : "sender@example.com"}
              />
            </div>

            <div className="upload-form-group">
              <label className="upload-label" htmlFor="date">
                Date {!selectedEmailFile && <span style={{color: 'red'}}>*</span>}
              </label>
              <input
                className="upload-input"
                type="date"
                id="date"
                name="date"
                value={formData.date || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className="upload-form-group">
              <label className="upload-label" htmlFor="body">
                Email Body {!selectedEmailFile && <span style={{color: 'red'}}>*</span>}
              </label>
              <textarea
                className="upload-textarea"
                id="body"
                name="body"
                value={formData.body || ''}
                onChange={handleInputChange}
                placeholder={selectedEmailFile ? "Will be extracted from file" : "Enter email content"}
              />
            </div>

            <div className="upload-row">
              <div className="upload-form-group upload-col">
                <label className="upload-label" htmlFor="contentType">Content Type</label>
                <select
                  className="upload-select"
                  id="contentType"
                  name="contentType"
                  value={formData.contentType}
                  onChange={handleInputChange}
                >
                  <option value="Email">Email</option>
                  <option value="PDF">PDF</option>
                  <option value="Website">Website</option>
                  <option value="Video">Video</option>
                  <option value="Meeting Notes">Meeting Notes</option>
                  <option value="Study">Study</option>
                </select>
              </div>

              <div className="upload-form-group upload-col">
                <label className="upload-label" htmlFor="sourceType">Source Type</label>
                <select
                  className="upload-select"
                  id="sourceType"
                  name="sourceType"
                  value={formData.sourceType}
                  onChange={handleInputChange}
                >
                  {SOURCE_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            <button className="upload-button" type="submit" disabled={loading || (!selectedEmailFile && (!formData.subject || !formData.body))}>
              {loading ? 'Processing...' : selectedEmailFile ? 'Upload Email File' : 'Process Email'}
            </button>
          </form>
          </>
        )}

        {activeTab === 'video' && (
          <form className="upload-form" onSubmit={handleVideoSubmit}>
            <div className="upload-form-group">
              <label className="upload-label" htmlFor="videoUrl">Video URL</label>
              <input
                className="upload-input"
                type="url"
                id="videoUrl"
                name="videoUrl"
                value={formData.videoUrl}
                onChange={handleInputChange}
                placeholder="https://www.youtube.com/watch?v=... or any video URL"
                required
              />
            </div>

            <div className="upload-form-group">
              <label className="upload-label" htmlFor="title">Video Title (Optional - auto-generated if empty)</label>
              <input
                className="upload-input"
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Leave empty to auto-generate from video"
              />
            </div>

            <div className="upload-form-group">
              <label className="upload-label" htmlFor="description">Description (Optional - auto-generated if empty)</label>
              <textarea
                className="upload-textarea"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Leave empty to auto-generate"
              />
            </div>

            <div className="upload-row">
              <div className="upload-form-group upload-col">
                <label className="upload-label" htmlFor="contentType">Content Type</label>
                <select
                  className="upload-select"
                  id="contentType"
                  name="contentType"
                  value={formData.contentType}
                  onChange={handleInputChange}
                >
                  <option value="Video">Video</option>
                  <option value="YouTube">YouTube</option>
                  <option value="Presentation">Presentation</option>
                  <option value="Tutorial">Tutorial</option>
                  <option value="Webinar">Webinar</option>
                  <option value="Training">Training</option>
                </select>
              </div>

              <div className="upload-form-group upload-col">
                <label className="upload-label" htmlFor="sourceType">Source Type</label>
                <select
                  className="upload-select"
                  id="sourceType"
                  name="sourceType"
                  value={formData.sourceType}
                  onChange={handleInputChange}
                >
                  {SOURCE_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            <button className="upload-button" type="submit" disabled={loading}>
              {loading ? 'Processing...' : 'Process Video'}
            </button>
          </form>
        )}

        {activeTab === 'paste' && (
          <form className="upload-form" onSubmit={handlePasteSubmit}>
            <div className="upload-form-group">
              <label className="upload-label" htmlFor="pastedContent">Content</label>
              <textarea
                className="upload-textarea"
                id="pastedContent"
                name="pastedContent"
                value={formData.pastedContent}
                onChange={handleInputChange}
                placeholder="Paste your content here..."
                required
                rows={12}
                style={{ minHeight: '300px' }}
              />
            </div>

            <div className="upload-form-group">
              <label className="upload-label" htmlFor="title">Title</label>
              <input
                className="upload-input"
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter title for this content"
                required
              />
            </div>

            <div className="upload-form-group">
              <label className="upload-label" htmlFor="description">Description (Optional)</label>
              <textarea
                className="upload-textarea"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter description"
              />
            </div>

            <div className="upload-row">
              <div className="upload-form-group upload-col">
                <label className="upload-label" htmlFor="contentType">Content Type</label>
                <select
                  className="upload-select"
                  id="contentType"
                  name="contentType"
                  value={formData.contentType}
                  onChange={handleInputChange}
                >
                  <option value="Text">Text</option>
                  <option value="Meeting Notes">Meeting Notes</option>
                  <option value="Email">Email</option>
                  <option value="Website">Website</option>
                  <option value="PDF">PDF</option>
                </select>
              </div>

              <div className="upload-form-group upload-col">
                <label className="upload-label" htmlFor="sourceType">Source Type</label>
                <select
                  className="upload-select"
                  id="sourceType"
                  name="sourceType"
                  value={formData.sourceType}
                  onChange={handleInputChange}
                >
                  {SOURCE_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            <button className="upload-button" type="submit" disabled={!formData.pastedContent || !formData.title || loading}>
              {loading ? 'Uploading...' : 'Upload Content'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Upload;
